// Platform detection and token persistence. Native Capacitor Preferences uses
// application preferences; it is not a cryptographic credential vault. Mirror
// the persisted token into an in-memory cache
// so the synchronous getToken() path used across the API client keeps working.
// On web we fall back to localStorage exactly as before.
import { Capacitor } from "@capacitor/core";

export const isNativeApp = (): boolean => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
};

const TOKEN_KEY = "kpv3_token";
let memToken: string | null = null;

// Prime the in-memory token from native preferences at startup (async).
export async function primeNativeToken(): Promise<void> {
  if (!isNativeApp()) return;
  try {
    const { Preferences } = await import("@capacitor/preferences");
    const { value } = await Preferences.get({ key: TOKEN_KEY });
    memToken = value ?? null;
  } catch { /* keep memToken as-is */ }
}

/** Synchronous token read used by the API client. */
export function readToken(): string | null {
  if (isNativeApp()) return memToken;
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

/** Resolve only after platform persistence succeeds; callers must await sign-out cleanup. */
export async function writeToken(token: string | null): Promise<void> {
  if (isNativeApp()) {
    const { Preferences } = await import("@capacitor/preferences");
    if (token) await Preferences.set({ key: TOKEN_KEY, value: token });
    else await Preferences.remove({ key: TOKEN_KEY });
    memToken = token;
    return;
  }
  token ? localStorage.setItem(TOKEN_KEY, token) : localStorage.removeItem(TOKEN_KEY);
}
